import { Lot } from "@prisma/client";

type SearchableLot = Lot & { [key: string]: any };

export class LotSearcher<T extends SearchableLot> {
    private lots: T[];
    // The smallest difference in scores that is considered significant.
    // This is used to determine 'near-ties' (which are broken by catalogNumber)
    // in the sort function.
    private epsilon: number = 3; 

    constructor(lots: T[]) {
        this.lots = lots;
    }

    public search(searchTerm: string | null | undefined): T[] {
        if (!searchTerm?.trim()) {
            return this.lots.sort((a, b) => {
                const catA = a.catalogNumber ?? Number.MAX_SAFE_INTEGER;
                const catB = b.catalogNumber ?? Number.MAX_SAFE_INTEGER;
                return catA - catB;
            });
        }

        const isNumericSearch = /^\d+$/.test(searchTerm);

        if (isNumericSearch) {
            const numberResult = this.lots.filter(lot => lot.catalogNumber?.toString() === searchTerm);
            if (numberResult.length > 0) {
                return numberResult;
            }
        }

        const ret =  this.lots
            .map(lot => ({
                lot,
                score: this.calculateSearchScore(lot, searchTerm, isNumericSearch),
            }))
            .filter(item => item.score > 0)
            .sort((a, b) => {
                const scoreDiff = b.score - a.score;
        
                // If scores are not close, sort by score. A difference of 1 is the smallest significant diff.
                if (Math.abs(scoreDiff) > this.epsilon) {
                    return scoreDiff;
                }
        
                // For ties or near-ties, prioritize lower catalog number.
                const catA = a.lot.catalogNumber ?? Number.MAX_SAFE_INTEGER;
                const catB = b.lot.catalogNumber ?? Number.MAX_SAFE_INTEGER;
                return catA - catB;
            })
            .map(item => item.lot);
        return ret;
    }

    private calculateSearchScore(lot: T, searchTerm: string, isNumericSearch: boolean): number {
        const searchTermLower = searchTerm.toLowerCase();
        let totalScore = 0;

        const fieldWeights: { [key: string]: number } = {
            title: 200,
            description: 50,
            category: 25,
        };

        if (isNumericSearch) {
            fieldWeights.catalogNumber = 1000;
        }

        for (const field in fieldWeights) {
            const value = lot[field];
            const weight = fieldWeights[field];

            if (value) {
                if (field === 'catalogNumber') {
                    if (value.toString().includes(searchTerm)) {
                        totalScore += weight;
                    }
                } else if (typeof value === "string") {
                    totalScore += this.calculateFieldScore(value, searchTermLower, weight);
                }
            }
        }
        return totalScore;
    }

    private calculateFieldScore(fieldValue: string, searchTerm: string, fieldWeight: number): number {
        const fieldValueLower = fieldValue.toLowerCase();
        const matchIndex = fieldValueLower.indexOf(searchTerm);
        if (matchIndex === -1) return 0;
    
        // Prioritize matches at the beginning of the string.
        const positionScore = 1 / (matchIndex + 1);
    
        // Add a bonus for whole-word matches.
        let wordBonus = 0;
        const searchTermEndIndex = matchIndex + searchTerm.length;
        const isStartOfWord = (matchIndex === 0) || !/\\w/.test(fieldValueLower[matchIndex - 1]);
        const isEndOfWord = (searchTermEndIndex === fieldValueLower.length) || !/\\w/.test(fieldValueLower[searchTermEndIndex]);
    
        if (isStartOfWord && isEndOfWord) {
            wordBonus = 1; // Give a significant bonus for matching a whole word.
        }
        
        const score = positionScore + wordBonus;
    
        return score * fieldWeight;
    }
} 